/**
 * Centralized Logging Service
 * Provides structured logging with different levels and environment-based configuration
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
}

const SENSITIVE_KEY_PATTERN = /(password|passphrase|token|session|secret|authorization|cookie|email|firstName|lastName|dateOfBirth|address|phone|ssn|otp|mfa|code)/i

class Logger {
  constructor () {
    this.level = this.getLogLevel()
    this.isProduction = process.env.NODE_ENV === 'production'
  }

  getLogLevel () {
    const envLevel = process.env.VUE_APP_LOG_LEVEL || 'INFO'
    return LOG_LEVELS[envLevel.toUpperCase()] ?? LOG_LEVELS.INFO
  }

  sanitizeContext (value, depth = 0) {
    if (depth > 5) {
      return '[TRUNCATED]'
    }

    if (value === null || value === undefined) {
      return value
    }

    if (Array.isArray(value)) {
      return value.map(item => this.sanitizeContext(item, depth + 1))
    }

    if (typeof value !== 'object') {
      return value
    }

    const sanitized = {}
    for (const [key, nestedValue] of Object.entries(value)) {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        sanitized[key] = '[REDACTED]'
      } else {
        sanitized[key] = this.sanitizeContext(nestedValue, depth + 1)
      }
    }

    return sanitized
  }

  formatMessage (level, message, context = {}) {
    const timestamp = new Date().toISOString()
    const sanitizedContext = this.sanitizeContext(context)
    return {
      timestamp,
      level,
      message,
      context: sanitizedContext,
      environment: process.env.NODE_ENV
    }
  }

  shouldLog (level) {
    return LOG_LEVELS[level] <= this.level
  }

  error (message, context = {}) {
    if (this.shouldLog('ERROR')) {
      const logEntry = this.formatMessage('ERROR', message, context)
      const safeContext = logEntry.context

      if (this.isProduction) {
        // In production, you might want to send to external logging service
        // For now, we'll still use console.error but structured
        console.error(JSON.stringify(logEntry))
      } else {
        console.error(`[ERROR] ${message}`, safeContext)
      }
    }
  }

  warn (message, context = {}) {
    if (this.shouldLog('WARN')) {
      const logEntry = this.formatMessage('WARN', message, context)
      const safeContext = logEntry.context

      if (this.isProduction) {
        console.warn(JSON.stringify(logEntry))
      } else {
        console.warn(`[WARN] ${message}`, safeContext)
      }
    }
  }

  info (message, context = {}) {
    if (this.shouldLog('INFO')) {
      const logEntry = this.formatMessage('INFO', message, context)
      const safeContext = logEntry.context

      if (this.isProduction) {
        console.info(JSON.stringify(logEntry))
      } else {
        console.info(`[INFO] ${message}`, safeContext)
      }
    }
  }

  debug (message, context = {}) {
    if (this.shouldLog('DEBUG')) {
      if (!this.isProduction) {
        const safeContext = this.sanitizeContext(context)
        console.debug(`[DEBUG] ${message}`, safeContext)
      }
    }
  }
}

// Create singleton instance
const logger = new Logger()

export default logger
